import type { JSX, ReactNode } from 'react';
import { MenuItem, TextField } from '@mui/material';
import type { RecurringItemTaxCodeDto } from '@pflegenavi/shared/api';

const NONE_VALUE = '__none__';

export function formatTaxCodeLabel(tc: RecurringItemTaxCodeDto): string {
  const suffix = tc.paragraph ? `: ${tc.paragraph}` : '';
  const percentage = parseFloat(tc.tax_amount) * 100;
  return `${tc.tax_name}${suffix} (${percentage}%)`;
}

export interface TaxCodePickerProps<T extends { id: string }> {
  taxCodes: T[];
  value: string | null;
  onChange: (taxCode: T | null) => void;
  getOptionLabel: (tc: T) => string;
  label: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  allowNone?: boolean;
  noneLabel?: ReactNode;
  error?: boolean;
  helperText?: ReactNode;
  name?: string;
  id?: string;
}

export const TaxCodePicker = <T extends { id: string }>({
  taxCodes,
  value,
  onChange,
  getOptionLabel,
  label,
  size,
  fullWidth,
  disabled,
  allowNone,
  noneLabel,
  error,
  helperText,
  name,
  id,
}: TaxCodePickerProps<T>): JSX.Element => {
  const selectValue = value ?? (allowNone ? NONE_VALUE : '');

  return (
    <TextField
      select
      id={id}
      name={name}
      label={label}
      value={selectValue}
      onChange={(e) => {
        const v = e.target.value;
        if (v === NONE_VALUE || v === '') {
          onChange(null);
          return;
        }
        const picked = taxCodes.find((tc) => tc.id === v) ?? null;
        onChange(picked);
      }}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      error={error}
      helperText={helperText}
    >
      {allowNone && (
        <MenuItem value={NONE_VALUE}>
          <em>{noneLabel}</em>
        </MenuItem>
      )}
      {taxCodes.map((tc) => (
        <MenuItem key={tc.id} value={tc.id}>
          {getOptionLabel(tc)}
        </MenuItem>
      ))}
    </TextField>
  );
};
